import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import {
    Icon,
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import { Image } from '@thoughtspot/radiant-react/widgets/image';
import {
    Horizontal,
    Vertical,
} from '@thoughtspot/radiant-react/widgets/layout';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import cx from 'classnames';
import Lottie from 'lottie-react';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useState } from 'react';
import { checklistDataTestId } from '../trial-info.util';
import styles from './checklist-modal.module.scss';
import { ChecklistTaskType } from './checklist-modal.types';

interface ChecklistTaskProps {
    content: ChecklistTaskType;
    isSubtask: boolean;
    checkedItems: Set<string>;
    onChecklistTaskButtonClick: (url: string, checkboxId: string) => void;
    lastCompletedTaskId?: string | null;
}

export const ChecklistTask: React.FC<ChecklistTaskProps> = ({
    content,
    isSubtask,
    checkedItems,
    onChecklistTaskButtonClick,
    lastCompletedTaskId,
}) => {
    const checkboxId = content.id;
    const { t } = useTranslation();

    const isJustCompleted = lastCompletedTaskId === checkboxId;
    const [showAnimation, setShowAnimation] = useState(isJustCompleted);
    const [animationData, setAnimationData] = useState<object | null>(null);

    useEffect(() => {
        if (isJustCompleted) {
            setShowAnimation(true);
        }
    }, [isJustCompleted]);

    useEffect(() => {
        if (!showAnimation || animationData) return;

        fetch('/img/lottie/checklist-item-animation.json')
            .then(res => res.json())
            .then(setAnimationData)
            .catch(() => setShowAnimation(false));
    }, [showAnimation, animationData]);

    const renderSubtaskIcon = () => {
        if (showAnimation && animationData) {
            return (
                <Lottie
                    animationData={animationData}
                    loop={false}
                    autoplay
                    className={styles.subtaskAnimation}
                    onComplete={() => setShowAnimation(false)}
                    onDataFailed={() => setShowAnimation(false)}
                />
            );
        }

        return (
            <Icon
                id={IconID.CHECKMARK_CIRCLE}
                color={
                    checkedItems.has(checkboxId)
                        ? IconColor.PURPLE
                        : IconColor.LIGHTGRAY
                }
                size={IconSize.MEDIUM}
                className={styles.subtaskIconButton}
            />
        );
    };

    const renderDescriptionContent = () =>
        content?.description ? (
            <Markdown className={styles.checklistListDescription}>
                {t(content.description)}
            </Markdown>
        ) : null;

    return (
        <Horizontal>
            {isSubtask && renderSubtaskIcon()}
            <Vertical
                className={cx(
                    isSubtask && styles.subtaskContent,
                    styles.taskContentContainer,
                )}
            >
                {isSubtask && (
                    <Typography
                        variant="p-bold"
                        className={cx(
                            styles.checklistItemDescription,
                            styles.subtaskHeading,
                        )}
                    >
                        {t(content.name)}
                    </Typography>
                )}
                {renderDescriptionContent()}
                {content?.media_urls &&
                    content.media_urls.map((media: string) => (
                        <Image
                            src={media}
                            alt={content.name}
                            className={styles.checklistItemMedia}
                            key={media}
                        />
                    ))}
                {content?.primary_button_configuration && (
                    <Button
                        text={t(content.primary_button_configuration?.text)}
                        type="secondary"
                        size="s"
                        onClick={() =>
                            onChecklistTaskButtonClick(content.type, checkboxId)
                        }
                        id={content?.id}
                        data-testid={
                            checklistDataTestId.CHECKLIST_TASK_SEE_HOW_BUTTON
                        }
                    />
                )}
            </Vertical>
        </Horizontal>
    );
};
